import { AnimateModal, ModalOpenProps } from '@/components/ui/modal';
import { Spinner } from '@/components/ui/spinner';
import { DRAWING_SUMMARY } from '@/config';

import { TakeoffTopTools } from '../components';
import { TakeoffStats } from '../components/summary-left-stats';
import {
  TakeoffSubGroup,
  TakeoffCategoryGroup,
} from '../components/summary-sub-group';
import { useSymbolStatistics } from '../hooks/useSymbolStatistics';
import {
  useSymbolSubTotal,
  useRemoteSummaryReport,
} from '../hooks/useSymbolSummary';
import { useTableScrollHandler } from '../hooks/useTableScrollHandler';
import { useTakeoffTools } from '../hooks/useTakeoffTools';
import { BPSymbol, SymbolEditParams } from '../types';

type SummaryModalProps = {
  symbols: BPSymbol[];
  context: SymbolEditParams;
} & ModalOpenProps;

/**
 * Drawing symbols overview report
 */
export const SummarySubTotal = ({
  isOpen,
  isOnStage,
  lazyCloseHandler,
  symbols,
  context,
}: SummaryModalProps) => {
  // ====== main logic with hooks ======

  const {
    togglePreview,
    downloading,
    creating,
    printing,
    downloadReportAs,
    generateSEVReport,
    generateReportForVendor,
    printMarkedupPDF,
  } = useTakeoffTools(context);

  const { subTotalGroups } = useSymbolSubTotal(symbols || []);
  // Monitor table scrolling to fix group header!
  useTableScrollHandler(subTotalGroups, !togglePreview);

  const {
    fittingCount,
    lengthCount,
    equipmentCount,
    completionDegree,
    inCompletionDegree,
  } = useSymbolStatistics(symbols || []);

  const { projectId, fileId, pageId, jobId } = context;
  const { symbolGroups, loading } = useRemoteSummaryReport(
    projectId,
    fileId,
    pageId,
    jobId,
  );

  // ====== end of hooks ======

  return (
    <AnimateModal
      isOpen={isOpen}
      isOnStage={isOnStage}
      lazyCloseHandler={lazyCloseHandler}
      title={DRAWING_SUMMARY}
      containerClass="full-screen-summary"
      tools={
        <TakeoffTopTools
          downloading={downloading}
          creating={creating}
          printing={printing}
          togglePreview={togglePreview}
          exportTo={downloadReportAs}
          exportSEV={generateSEVReport}
          outputTo={generateReportForVendor}
          printLocal={printMarkedupPDF}
        />
      }
    >
      <div className="flex size-full py-2">
        {/* left column */}
        <TakeoffStats
          symbols={symbols}
          fittingCount={fittingCount}
          lengthCount={lengthCount}
          equipmentCount={equipmentCount}
          completionDegree={completionDegree}
          inCompletionDegree={inCompletionDegree}
        />
        {/* right column */}
        {/* Add additional pb-8 to reveal last row of symbol groupd regarding issue: #610 - 2024/07/10 */}
        <div className="right-panel-table w-full summary-column p-2 border-t border-gray-300 pb-8">
          {/* === BACKEND GENERATED REPORT */}
          {togglePreview &&
            symbolGroups.map((row, index) => (
              <TakeoffCategoryGroup key={index} row={row} />
            ))}
          {/* === INTERACTIVE SYMBOLS REPORT === */}
          {!togglePreview &&
            subTotalGroups.map((group, index) => (
              <TakeoffSubGroup
                key={index}
                group={group}
                lazyCloseHandler={lazyCloseHandler}
              />
            ))}
          {loading && (
            <div className="h-30 w-36 mx-1">
              <Spinner size="sm" className="mt-1 inline-block" />
              <span className=" ml-1 text-xs inline-block text-gray-600 align-bottom">
                loading reqport...
              </span>
            </div>
          )}
        </div>
      </div>
    </AnimateModal>
  );
};
